import * as React from 'react';
import { DollarSign, AlertCircle, Check, BarChart3 } from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';
import BadgeComponent from '../ui/BadgeComponent';

interface WizardStep3Props {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

export default function WizardStep3({ data, updateData }: WizardStep3Props) {
  const isEligibleForMidRoll = data.mediaType === 'video'; // In real app, check video duration > 8 min

  const handleAdFormatToggle = (format: keyof WizardData['adFormats']) => {
    updateData({
      adFormats: {
        ...data.adFormats,
        [format]: !data.adFormats[format],
      },
    });
  };

  const estimatedRevenue = () => {
    let base = 0;
    if (data.adFormats.display) base += 10;
    if (data.adFormats.overlay) base += 15;
    if (data.adFormats.skippable) base += 25;
    if (data.adFormats.nonSkippable) base += 40;
    if (data.adFormats.midRoll) base += 50;
    return base;
  };

  return (
    <div className="space-y-8">
      {/* Monetization Toggle */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 border border-green-200 rounded flex items-center justify-center">
                <DollarSign size={20} className="text-green-700" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#111827]">Enable Monetization</h3>
                <p className="text-xs text-[#6B7280]">Earn revenue from ads on this video</p>
              </div>
            </div>
            <label className="relative inline-block w-12 h-6">
              <input
                type="checkbox"
                checked={data.monetizationEnabled}
                onChange={(e) => updateData({ monetizationEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <span className="absolute inset-0 bg-[#D1D5DB] rounded-full peer-checked:bg-[#10B981] transition-colors cursor-pointer"></span>
              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6 cursor-pointer shadow-sm"></span>
            </label>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-blue-900 mb-1">Monetization Eligibility</div>
              <div className="text-xs text-blue-700">
                Your video meets the requirements for monetization. You have 4,500 watch hours and 1,200 subscribers.
              </div>
            </div>
          </div>
        </div>
      </CardComponent>

      {/* Ad Formats */}
      {data.monetizationEnabled && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-medium text-[#111827]">Ad Formats</h3>
            <p className="text-xs text-[#6B7280] mt-1">Select which ad types can appear on your video</p>
          </div>
          <div className="p-6 space-y-4">
            {/* Display Ads */}
            <label className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={data.adFormats.display}
                onChange={() => handleAdFormatToggle('display')}
                className="mt-1 w-4 h-4 text-[#2563EB] border-[#D1D5DB] rounded focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#111827]">Display ads</span>
                  <BadgeComponent variant="default">Low impact</BadgeComponent>
                </div>
                <p className="text-xs text-[#6B7280]">Banner ads that appear beside your video</p>
              </div>
            </label>

            {/* Overlay Ads */}
            <label className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={data.adFormats.overlay}
                onChange={() => handleAdFormatToggle('overlay')}
                className="mt-1 w-4 h-4 text-[#2563EB] border-[#D1D5DB] rounded focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#111827]">Overlay ads</span>
                  <BadgeComponent variant="default">Low impact</BadgeComponent>
                </div>
                <p className="text-xs text-[#6B7280]">Semi-transparent overlay ads on the lower portion of video</p>
              </div>
            </label>

            {/* Skippable Ads */}
            <label className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={data.adFormats.skippable}
                onChange={() => handleAdFormatToggle('skippable')}
                className="mt-1 w-4 h-4 text-[#2563EB] border-[#D1D5DB] rounded focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#111827]">Skippable video ads</span>
                  <BadgeComponent variant="warning">Medium impact</BadgeComponent>
                </div>
                <p className="text-xs text-[#6B7280]">Video ads that viewers can skip after 5 seconds</p>
              </div>
            </label>

            {/* Non-Skippable Ads */}
            <label className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={data.adFormats.nonSkippable}
                onChange={() => handleAdFormatToggle('nonSkippable')}
                className="mt-1 w-4 h-4 text-[#2563EB] border-[#D1D5DB] rounded focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#111827]">Non-skippable video ads</span>
                  <BadgeComponent variant="default">High impact</BadgeComponent>
                </div>
                <p className="text-xs text-[#6B7280]">15-20 second video ads that must be watched</p>
              </div>
            </label>

            {/* Mid-Roll Ads */}
            <label
              className={`flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg ${
                isEligibleForMidRoll ? 'hover:bg-[#F9FAFB] cursor-pointer' : 'opacity-50 cursor-not-allowed'
              } transition-colors`}
            >
              <input
                type="checkbox"
                checked={data.adFormats.midRoll}
                onChange={() => handleAdFormatToggle('midRoll')}
                disabled={!isEligibleForMidRoll}
                className="mt-1 w-4 h-4 text-[#2563EB] border-[#D1D5DB] rounded focus:ring-[#2563EB] focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#111827]">Mid-roll ads</span>
                  <BadgeComponent variant="default">Highest revenue</BadgeComponent>
                </div>
                <p className="text-xs text-[#6B7280]">
                  {isEligibleForMidRoll
                    ? 'Ads that appear during your video (requires 8+ min video)'
                    : 'Only available for videos longer than 8 minutes'}
                </p>
              </div>
            </label>
          </div>
        </CardComponent>
      )}

      {/* Eligibility Conditions */}
      {data.monetizationEnabled && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-medium text-[#111827]">Eligibility Status</h3>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Copyright status</span>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <span className="text-sm font-medium text-[#111827]">Clear</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Age restriction impact</span>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <span className="text-sm font-medium text-[#111827]">No restrictions</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Limited ads warnings</span>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <span className="text-sm font-medium text-[#111827]">None</span>
              </div>
            </div>
          </div>
        </CardComponent>
      )}

      {/* Revenue Preview */}
      {data.monetizationEnabled && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-[#6B7280]" />
              <h3 className="text-sm font-medium text-[#111827]">Estimated Revenue</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-semibold text-[#111827]">
                ${estimatedRevenue()}-{estimatedRevenue() * 2}
              </span>
              <span className="text-sm text-[#6B7280]">per 1,000 views</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B7280]">Ad placements</span>
                <span className="text-[#111827] font-medium">
                  {Object.values(data.adFormats).filter(Boolean).length} formats enabled
                </span>
              </div>
              <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#10B981] transition-all"
                  style={{ width: `${(estimatedRevenue() / 140) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </CardComponent>
      )}
    </div>
  );
}
